<script lang="ts">
  // BJL-CHECK: Button (used for close), Card. The Board CTA is the one custom control: it is
  // painted in the ROUTE's MTA color, which is data rather than a theme token, and every
  // Button variant paints from --bjl-color-*. See artifacts/deviations/VehicleSheet.md.
  import { Button } from '@byjackli-labs/ui/Button'
  import { Colors, getRouteColor, getRouteTextColor, routeBadgeLabel } from '../../ui/colors'
  import { bulletFontPx, bulletText, panelFrame, Type } from '../../ui/theme'
  import { safeBottom } from '../../ui/safeArea'
  import { useRiderStore } from '../../store/rider'
  import type { VehiclePosition } from '../../store/transit'
  import { api } from '../../services/api'
  import { createCrowding } from './crowding.svelte'
  import CrowdingDots from './CrowdingDots.svelte'
  import RouteBadge from '../RouteBadge.svelte'
  import RailRouteBadge from '../RailRouteBadge.svelte'
  import NjBusRouteBadge from '../NjBusRouteBadge.svelte'
  import TripTimetable from './TripTimetable.svelte'
  import type { TransferContext } from './timetable'
  import type { FerryLandingInfo, NjBusStopInfo, RailStationInfo } from './stationInfo'
  import { njBusPositionKind } from './njBusMotion'
  import { formatShortClock } from '../../ui/transitTime'
  import { serverNowSec } from '../../services/serverClock'

  interface Props {
    vehicle: VehiclePosition
    onClose: () => void
    /** A ferry's timetable offers "Transfer Here" at each landing; this opens that landing. */
    onOpenFerryLanding?: (landing: FerryLandingInfo, ctx: TransferContext | null) => void
    /** A LIRR / Metro-North train's timetable does the same for its stations. */
    onOpenRailStation?: (station: RailStationInfo, ctx: TransferContext | null) => void
    /** An NJ Transit bus's timetable does the same for its stops. */
    onOpenNjBusStop?: (stop: NjBusStopInfo, ctx: TransferContext | null) => void
  }

  const { vehicle, onClose, onOpenFerryLanding, onOpenRailStation, onOpenNjBusStop }: Props = $props()

  // A ferry can't be boarded (yet): the riding panel's two faces are the subway car front
  // and the bus LCD, and ride tracking follows a train's trip updates or a bus's SIRI calls.
  // So a tapped boat shows where it is going and its remaining landings instead.
  const isFerry = $derived(vehicle.type === 'ferry')
  // A LIRR / Metro-North train likewise: boarding is the subway and bus's ride tracking, and
  // neither face fits a railroad. A tapped train shows its remaining stations.
  const isRail = $derived(vehicle.type === 'rail')
  // An NJ Transit bus too: ride tracking follows MTA buses through SIRI, which knows nothing
  // of NJ Transit. A tapped bus shows its remaining stops from NJ Transit's own timetable.
  const isNjBus = $derived(vehicle.type === 'njbus')

  const STATUS_LABEL: Record<VehiclePosition['status'], string> = {
    STOPPED_AT: 'Stopped',
    INCOMING_AT: 'Arriving',
    IN_TRANSIT_TO: 'En Route',
  }

  function bearingToDirection(bearing: number): string {
    const b = ((bearing % 360) + 360) % 360
    if (b >= 315 || b < 135) return 'Uptown'
    return 'Downtown'
  }

  // What an NJ Transit bus's drawn position IS, by the map's own rule (njBusPositionKind):
  // laying over at its terminal, estimated from its timetable, or a last sighting. Say so here.
  // Ticks on its own, because a bus that has stopped reporting is exactly the one no socket
  // push will refresh.
  let nowSec = $state(serverNowSec())
  $effect(() => {
    if (!isNjBus) return
    const id = setInterval(() => { nowSec = serverNowSec() }, 15_000)
    return () => clearInterval(id)
  })
  const staleNote = $derived.by(() => {
    if (!isNjBus) return null
    const kind = njBusPositionKind(vehicle.timestamp, nowSec, {
      layoverUntil: vehicle.layoverUntil,
      estimable: (vehicle.upcomingCalls?.length ?? 0) >= 2,
    })
    const quietMin = vehicle.timestamp === undefined ? 0 : Math.floor((nowSec - vehicle.timestamp) / 60)
    switch (kind) {
      case 'layover':
        return `Laying over — departs ${formatShortClock(vehicle.layoverUntil!)}`
      case 'estimated':
        return `Estimated from the timetable — no GPS report for ${quietMin} min`
      case 'lastEstimate':
        return `No GPS report for ${quietMin} min — last estimated position`
      case 'lastSighting':
        return `No GPS report for ${quietMin} min — last known position`
      default:
        return null
    }
  })

  const crowding = createCrowding(() => vehicle)
  const routeColor = $derived(getRouteColor(vehicle.routeId))

  let lastStopName = $state<string | null>(null)

  // Keyed off tripId/type, not the vehicle object, which is replaced on every socket push.
  const tripId = $derived(vehicle.tripId)
  const type = $derived(vehicle.type)

  $effect(() => {
    // Buses and ferries carry their destination as a headsign; only subways need the
    // trip-update lookup for their last stop.
    if (!tripId || type !== 'subway') return
    let live = true
    api
      .getTripUpdates(tripId)
      .then((data) => {
        const last = data.updates[data.updates.length - 1]
        if (live && last) lastStopName = last.stopName ?? last.stopId
      })
      .catch(() => {})
    return () => { live = false }
  })

  // Buses show their terminal rather than uptown/downtown, which means nothing for a bus.
  const headingText = $derived.by(() => {
    if (vehicle.type !== 'subway') {
      return vehicle.destinationName ? `To ${vehicle.destinationName}` : 'In service'
    }
    const direction = bearingToDirection(vehicle.bearing)
    return lastStopName ? `${direction} to ${lastStopName}` : direction
  })

  const isExpress = $derived(vehicle.routeId.includes('X'))

  // This sheet opens on whatever was tapped, so its bullet carries bus ids as readily as
  // subway ones: "M14A-SBS" is five times the width of a "6" at the same size, and at a
  // fixed 16px it wrapped into two rows that both broke the disc. 16px is now the ceiling a
  // short id gets; a wider one is measured down until it clears the rim.
  const BULLET_BOX = 40
  const bulletLabel = $derived(routeBadgeLabel(vehicle.routeId))
  const bulletPx = $derived(
    bulletFontPx(bulletLabel, BULLET_BOX, 16, isExpress ? 'diamond' : 'circle'),
  )

  const VEHICLE_ICON = { subway: '🚇', bus: '🚌', ferry: '⛴️', rail: '🚆', njbus: '🚌' } as const
  // A railroad train is known by its number, and neither feed publishes speed — so the stats
  // are the number, how late it is, and its heading.
  const lateMin = $derived(Math.round((vehicle.delaySeconds ?? 0) / 60))
  const lateness = $derived(lateMin >= 2
    ? { value: `${lateMin}`, label: 'min late' }
    : { value: vehicle.delaySeconds === undefined ? '—' : '✓', label: 'on time' })
  const stats = $derived(isRail ? [
    { value: vehicle.label ?? '—', label: 'train' },
    lateness,
    { value: `${vehicle.bearing.toFixed(0)}°`, label: 'bearing' },
  ] : isNjBus ? [
    // NJ Transit's fleet number, as painted on the bus, and how late it runs — then its speed,
    // which the backend derives from successive fixes (the feed sends none), once it has two.
    { value: vehicle.label ?? '—', label: 'bus' },
    lateness,
    vehicle.speed !== undefined
      ? { value: `${Math.round(vehicle.speed)}`, label: 'mph' }
      : { value: `${vehicle.bearing.toFixed(0)}°`, label: 'bearing' },
  ] : [
    { value: `${Math.round(vehicle.speed ?? 0)}`, label: 'mph' },
    { value: `${vehicle.bearing.toFixed(0)}°`, label: 'bearing' },
    // A boat's hull number is painted on it (and is what NYC Ferry's own app names it by).
    isFerry && vehicle.label
      ? { value: vehicle.label, label: 'vessel' }
      : { value: VEHICLE_ICON[vehicle.type], label: vehicle.type },
  ])

  function handleBoard() {
    // Watching stops itself: store/vehicleWatch.ts subscribes to the rider store, so
    // every board entry point gets it, not just this one.
    useRiderStore.getState().startRide(vehicle.vehicleId, vehicle)
    onClose()
  }
</script>

<div
  style="
    position: fixed; bottom: 0; left: 0; right: 0;
    background-color: {Colors.ground};
    {panelFrame('top')}
    max-height: {isFerry || isRail || isNjBus ? '70vh' : '55vh'};
    z-index: 1000;
    display: flex; flex-direction: column;
  "
>
  <!-- Close button pinned to top-right -->
  <Button
    variant="text"
    size="sm"
    iconOnly
    onclick={onClose}
    aria-label="Close vehicle details"
    style="position: absolute; top: 8px; right: 8px; z-index: 1;"
  >✕</Button>

  <!-- Info section -->
  <div
    style="padding: 16px 16px {safeBottom(16)}; display: flex; flex-direction: column; gap: 14px;"
  >
    <div style="display: flex; align-items: center; gap: 12px;">
      {#if isRail}
        <RailRouteBadge routeId={vehicle.routeId} size={36} />
      {:else if isNjBus}
        <NjBusRouteBadge routeId={vehicle.routeId} size="md" />
      {:else}
        <RouteBadge routeId={vehicle.routeId} size={40} />
      {/if}

      <div style="flex: 1; padding-right: 32px;">
        <!-- Route name/text + crowding dots side by side, per design. -->
        <div style="display: flex; align-items: center; gap: 10px;">
          <div style="color: {Colors.text}; {Type.title};">
            {headingText}
          </div>
          <CrowdingDots {crowding} accentColor={routeColor} />
        </div>
        <div style="color: {Colors.textSecondary}; {Type.body}; margin-top: 2px;">
          {STATUS_LABEL[vehicle.status]}
        </div>
        {#if staleNote}
          <div style="color: {Colors.textMuted}; {Type.caption}; margin-top: 2px;">{staleNote}</div>
        {/if}
      </div>
    </div>

    <!-- Stats row -->
    <div
      style="
        display: flex; justify-content: space-around;
        background-color: {Colors.surfaceElevated};
        border-radius: var(--bjl-radius-md, 10px); padding: 12px;
      "
    >
      {#each stats as { value, label } (label)}
        <div style="display: flex; flex-direction: column; align-items: center; gap: 2px;">
          <span style="color: {Colors.text}; {Type.heading};">{value}</span>
          <span style="color: {Colors.textMuted}; {Type.caption};">{label}</span>
        </div>
      {/each}
    </div>

    {#if isFerry}
      <!-- The boat's run: timetable with the live feed laid over it, marking where it is
           now. Scrolls inside the sheet so the header and stats stay put. -->
      {#if vehicle.tripId}
        <div style="max-height: 38vh; overflow-y: auto; margin: 0 -4px;">
          <TripTimetable
            source={{ kind: 'ferry', tripId: vehicle.tripId }}
            routeId={vehicle.routeId}
            onTransfer={(stopId, stopName, ctx) => {
              onOpenFerryLanding?.({ id: stopId, name: stopName }, ctx)
              onClose()
            }}
          />
        </div>
      {/if}
    {:else if isRail}
      <!-- The train's run, the same way: timetable with both railroads' live feed laid over it. -->
      {#if vehicle.tripId}
        <div style="max-height: 38vh; overflow-y: auto; margin: 0 -4px;">
          <TripTimetable
            source={{ kind: 'rail', tripId: vehicle.tripId }}
            routeId={vehicle.routeId}
            onTransfer={(stopId, stopName, ctx) => {
              onOpenRailStation?.({ id: stopId, name: stopName }, ctx)
              onClose()
            }}
          />
        </div>
      {/if}
    {:else if isNjBus}
      <!-- The bus's run: NJ Transit's timetable with its trip updates laid over it. A bus the
           feed could not match to a trip has no run to show — say so rather than nothing. -->
      {#if vehicle.tripId}
        <div style="max-height: 38vh; overflow-y: auto; margin: 0 -4px;">
          <TripTimetable
            source={{ kind: 'njbus', tripId: vehicle.tripId }}
            routeId={vehicle.routeId}
            onTransfer={(stopId, stopName, ctx) => {
              onOpenNjBusStop?.({ id: stopId, name: stopName }, ctx)
              onClose()
            }}
          />
        </div>
      {:else}
        <div style="color: {Colors.textMuted}; {Type.body};">
          NJ Transit hasn’t matched this bus to a scheduled trip, so its stops aren’t known.
        </div>
      {/if}
    {:else}
    <!-- Boarding is the only action here. Reporting crowding lives in the rider panel,
         i.e. behind boarding: a rider who is actually on the vehicle is the only one who
         can answer honestly, so tapping a train on the map shows the aggregate
         (CrowdingDots) but can't vote. The 3D chase camera is likewise reached only from
         the riding panel's own toggle now — it used to be offered here as well. -->
    <button
      onclick={handleBoard}
      style="
        width: 100%;
        background-color: {routeColor}; border: none;
        border-radius: var(--bjl-radius-md, 12px);
        padding: 14px 0; cursor: pointer;
        color: {getRouteTextColor(vehicle.routeId)}; font-weight: 800; font-size: 16px;
      "
    >
      {VEHICLE_ICON[vehicle.type]} Board This Vehicle
    </button>
    {/if}
  </div>
</div>
